import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../types'


/**
 * Cutout utility helper functions
 * Used to calculate & generate cutout details in a shape
 */
export default class EditorCutoutUtils {


  /**
   * Calculate the cutout shape list between a segment list and a calculated convex hull & straight cutting line list
   * Note: Corner radiuses should be ignored when calculating the convexHullPointList data
   * 
   * Returns an array of Objects. Each object containing properties including:
   *    isCorner - If set, the cutout is considered a corner cutout. Otherwise an edge cutout
   *    isCutoutPolished - If set, the cutout has been flagged for polished
   *    startSegmentIndex - Start index of the segment on the panel where the cutout starts
   *    endSegmentIndex - End index of the segment on the panel where the cutout ends
   *    points - Array of Vector2 objects representing points from the start to end of the cutout
   *    segmentIdList - List of segment IDs that make up the cutout
   *    pathSegmentList - Path segments of the cutout. Corner radiuses are zerod & converted to arc segments
   *    displayPointList - Array of Vector2 objects for the display point list of the cutout path. Used for winding/orientation testing & CNC display
   */
  static generateCutoutListInConvexHull(segmentList, convexHullPointList, cuttingLineList) {
    let duplicateThreshold = 0.0001
    let duplicateAngleThreshold = 0.1
    let cornerCutoutAngleMinThreshold = 45.0
    let cornerCutoutAngleMaxThreshold = 175.0     // If edge-to-edge, the cutout is classed as an edge cutout
    let minJoinArcFraction = 0.25     // If cutout starts/ends along an arc, the fraction along the arc it needs to include the entire arc
    let maxLeaveArcFraction = 0.75    // If cutout starts/ends along an arc, the fraction along the arc it needs to include the entire arc
    let minJoinArcLength = 50         // Needs to join either 25% in (as above) or 50mm along
    let minLeaveArcLength = 50        // Needs to leave either 75% in (as above) or 50mm before end

    let shapeDivisions = Config.Settings.arcSubdivisions
    var i = 0

    if(!_.isArray(convexHullPointList) || convexHullPointList.length <= 2)
      return []

    // Cannot have cutouts if 2 sides or less (including arcs)
    if(!_.isArray(segmentList) || segmentList.length <= 2)
      return []

    // Setup hull info
    if(MathsUtils.is2DPolygonClosed(convexHullPointList))
      convexHullPointList.pop()

    let hullBounds = MathsUtils.calculate2DPointListBounds(convexHullPointList)
    let hullCenter = hullBounds.getCenter(new THREE.Vector2())

    // Go through all segments
    // If a glass segment is collinear with a hull edge, ignore the segment
    // If not, start a new cutout & run until a segment is not collinear with a hull edge
    // Stops, add the cutout to the list & repeats

    let createNewCutout = () => {
      return {
        isCorner: false,
        isCutoutPolished: false,
        isPointsReversed: false,
        startSegmentPointIndex: -1,
        endSegmentPointIndex: -1,
        startSegmentIndex: -1,
        endSegmentIndex: -1,
        points: [],
        segmentIdList: [],
        pathSegmentList: [],
        displayPointList: []
      }
    }

    var cutoutList = []
    var openCutout = createNewCutout()

    var segmentPointList = []
    segmentList.forEach((segment, segmentIndex) => {
      var segmentPoints = [segment.startPosition.clone(), segment.endPosition.clone()]
      
      if(segment.type == GlassDesignerTypes.SegmentTypes.arc && segment.arcEllipseCurve) {
        // Scale subdivisions by length, so tiny arcs don't get 20 points added
        let arcDivisions = Math.min(Math.max(segment.arcEllipseCurve.getLength() * 0.25, 2), Config.Settings.arcSubdivisions)

        let arcPointList = segment.arcEllipseCurve.getPoints(arcDivisions)
        segmentPoints = arcPointList
      }

      // Associate data with the points from the segment
      segmentPointList = segmentPointList.concat(segmentPoints.map(point => {
        return {
          isCutoutPolished: segment.isCutoutPolished,
          segmentIndex: segmentIndex,
          position: point
        }
      }))
    })

    // Remove duplicates
    if(segmentPointList.length > 1) {
      segmentPointList = MathsUtils.optimizePointList(segmentPointList, {
        optimizeLines: false,
        duplicateThreshold: duplicateThreshold,
        angleThreshold: duplicateAngleThreshold,
        pointTransformCallback: (item) => { return item.position }
      })
    }


    // List cannot be closed
    if(MathsUtils.is2DPolygonClosed(segmentPointList.map(point => point.position)))
      segmentPointList.pop()

    let closeCurrentCutout = () => {
      if(openCutout.points.length > 2) {
        // Force the cutout to be clockwise
        if(MathsUtils.get2DPolygonOrientation(openCutout.points) > 0) {
          openCutout.points.reverse()
          openCutout.isPointsReversed = true
        }

        // If the start & end directions of the hull segments this cutout is connected to are over 45 degrees direction difference, flag this cutout as a corner
        openCutout.isCorner = false
        if(openCutout.startSegmentPointIndex != -1 && openCutout.endSegmentPointIndex != -1) {
          var prevIndex = openCutout.startSegmentPointIndex == 0 ? segmentPointList.length-1 : openCutout.startSegmentPointIndex-1
          var nextIndex = (openCutout.endSegmentPointIndex+1) % segmentPointList.length

          let seg1A = segmentPointList[prevIndex].position
          let seg1B = segmentPointList[openCutout.startSegmentPointIndex].position
          let seg2A = segmentPointList[openCutout.endSegmentPointIndex].position
          let seg2B = segmentPointList[nextIndex].position

          let seg1Dir = new THREE.Vector2().subVectors(seg1B, seg1A)
          seg1Dir.normalize()
          let seg2Dir = new THREE.Vector2().subVectors(seg2B, seg2A)
          seg2Dir.normalize()

          // Note: The above doesn't cover all cases where a large edge-to-edge cutout clips a corner still (eg, rake)
          // If the start/end points don't connect to a single straight cutting line, then flag as a corner cutout
          if(!openCutout.isCorner) {
            let pointOnLineTolerance = 0.05
            let cutoutOnCutLine = cuttingLineList.find(cutLine => {
              var closestSubPntA = MathsUtils.getClosest2DPointOnLineSegment(cutLine.startPosition, cutLine.endPosition, seg1B)
              var closestSubPntB = MathsUtils.getClosest2DPointOnLineSegment(cutLine.startPosition, cutLine.endPosition, seg2A)

              if(closestSubPntA.distanceTo(seg1B) < pointOnLineTolerance && closestSubPntB.distanceTo(seg2A) < pointOnLineTolerance) {
                return true
              }

              return false
            })

            if(!cutoutOnCutLine) {
              // Final check: If the cutout ends are both connected to the same edge of the shape bounds, that is also not a corner cutout
              let cutoutOnBounds = false
              for(let edge=0; edge<4 && !cutoutOnBounds; edge++) {
                let edgePntStartOnBounds = MathsUtils.point2DOnBoundsEdgeSegment(hullBounds, edge, seg1B)
                let edgePntEndOnBounds = MathsUtils.point2DOnBoundsEdgeSegment(hullBounds, edge, seg2A)

                if(edgePntStartOnBounds && edgePntEndOnBounds) {
                  cutoutOnBounds = true
                  break
                }
              }

              if(!cutoutOnBounds)
                openCutout.isCorner = true
            }
          }


        } else {
          console.error(`[CUTOUT] Cutout has invalid start/end indices ${openCutout.startSegmentPointIndex} -> ${openCutout.endSegmentPointIndex}`)
        }
        
        cutoutList.push(openCutout)
      } else {
        if(openCutout.points.length > 0)
          console.warn(`[HULL] Warning, cutout has only ${openCutout.points.length} vertices`)
      }
      openCutout = createNewCutout()
    }

    // Start from top-left most vertex in the segment
    var segmentStartIndex = 0
    for(i=0; i<segmentPointList.length; i++) {
      if(segmentPointList[i].position.x < segmentPointList[segmentStartIndex].position.x) {
        segmentStartIndex = i
      } else if(Math.abs(segmentPointList[i].position.x - segmentPointList[segmentStartIndex].position.x) < 0.001) {
        if(segmentPointList[i].position.y < segmentPointList[segmentStartIndex].position.y) {
          segmentStartIndex = i
        }
      }
    }

    for(i=0; i<segmentPointList.length; i++) {
      let pnt1 = segmentPointList[(i+segmentStartIndex) % segmentPointList.length].position
      let pnt2 = segmentPointList[(segmentStartIndex+i+1) % segmentPointList.length].position

      let isPnt1Polished = segmentPointList[(i+segmentStartIndex) % segmentPointList.length].isCutoutPolished
      let isPnt2Polished = segmentPointList[(segmentStartIndex+i+1) % segmentPointList.length].isCutoutPolished

      let pnt1SegIndex = segmentPointList[(i+segmentStartIndex) % segmentPointList.length].segmentIndex
      let pnt2SegIndex = segmentPointList[(segmentStartIndex+i+1) % segmentPointList.length].segmentIndex

      var point1OnHull = false
      var point2OnHull = false

      for(var j=0; j<convexHullPointList.length; j++) {
        let hullPnt1 = convexHullPointList[j]
        let hullPnt2 = convexHullPointList[(j+1) % convexHullPointList.length]

        // Collinear with any part of the convex hull?
        point1OnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, pnt1, 0.01) || point1OnHull
        point2OnHull = MathsUtils.point2DOnLineSegment(hullPnt1, hullPnt2, pnt2, 0.01) || point2OnHull

        if(point1OnHull && point2OnHull)
          break
      }

      if(!point1OnHull && !point2OnHull) {
        openCutout.isCutoutPolished = openCutout.isCutoutPolished || isPnt1Polished || isPnt2Polished
        openCutout.points.push(pnt1)
      } else {
        // If exiting the hull, start a new one
        if(point1OnHull && !point2OnHull) {
          openCutout.isCutoutPolished = openCutout.isCutoutPolished || isPnt1Polished || isPnt2Polished

          // Close it if one already open
          if(openCutout.points.length > 0) {
            openCutout.points.push(pnt1)
            openCutout.endSegmentPointIndex = (i+segmentStartIndex) % segmentPointList.length
            openCutout.endSegmentIndex = pnt1SegIndex
            closeCurrentCutout()
          }

          // New cutout
          openCutout.startSegmentPointIndex = (i+segmentStartIndex) % segmentPointList.length
          openCutout.startSegmentIndex = pnt1SegIndex
          openCutout.points.push(pnt1)
        }

        // If entering the hull, finish it
        if(!point1OnHull && point2OnHull) {
          if(openCutout.points.length > 0) {

            openCutout.points.push(pnt1)
            openCutout.points.push(pnt2)
            openCutout.endSegmentPointIndex = (i+segmentStartIndex+1) % segmentPointList.length
            openCutout.isCutoutPolished = openCutout.isCutoutPolished || isPnt1Polished || isPnt2Polished
            openCutout.endSegmentIndex = pnt2SegIndex

            closeCurrentCutout()
          }
        }
      }
    }

    // Close any currently open cutout
    if(openCutout.points.length > 0 && openCutout.endSegmentPointIndex == -1) {
      openCutout.endSegmentPointIndex = (segmentPointList.length+segmentStartIndex) % segmentPointList.length
      openCutout.endSegmentIndex = segmentPointList[openCutout.endSegmentPointIndex].segmentIndex
    }
    closeCurrentCutout()

    // Generate the path from the start to end segments
    // Store the path for CNC purposes
    // Taking into account corner radiuses
    cutoutList.forEach(cutout => {
      var segmentCount = cutout.endSegmentIndex - cutout.startSegmentIndex
      if(cutout.endSegmentIndex < cutout.startSegmentIndex)
        segmentCount = (segmentList.length - cutout.startSegmentIndex) + cutout.endSegmentIndex

      for(var i=cutout.startSegmentIndex; i<=cutout.startSegmentIndex+segmentCount; i++) {
        var prevIndex = (i % segmentList.length) - 1
        if(prevIndex < 0)
          prevIndex += segmentList.length

        let segment = segmentList[i % segmentList.length].clone()
        let prevSegment = segmentList[prevIndex]
        let nextSegment = segmentList[(i+1) % segmentList.length].clone()

        // If point exists at start of segment, take into account this segment
        let useStartSeg = (i > cutout.startSegmentIndex) || MathsUtils.pointInList(cutout.points, segment.startPosition)
        let useEndSeg = (i < cutout.startSegmentIndex+segmentCount) || MathsUtils.pointInList(cutout.points, segment.endPosition)

        // If this is an arc & it started it along the path, consider the whole arc as part of the cutout
        // Handles cases with cutouts starting along arcs on the convex arc
        if(!useStartSeg && useEndSeg && segment.type == GlassDesignerTypes.SegmentTypes.arc) {
          let firstCutoutPoint = cutout.points[0]
          if(cutout.isPointsReversed)
            firstCutoutPoint = cutout.points[cutout.points.length-1]

          // If leaves hull at less than 75% of the arc (or less then 50mm from end), include the arc
          if(firstCutoutPoint && MathsUtils.isPointOnSegment(segment, firstCutoutPoint, 0.001)) {
            let leaveArcFraction = segment.calculatePointFractionOnSegment(firstCutoutPoint)
            let arcLength = segment.getLength()
            if(leaveArcFraction < maxLeaveArcFraction || leaveArcFraction * arcLength < arcLength-minLeaveArcLength)
              useStartSeg = true
          }
        }

        // If this is an arc & it joined it along the path, consider the whole arc as part of the cutout
        // Handles cases with cutouts finishing at arcs on the convex arc
        if(useStartSeg && !useEndSeg && segment.type == GlassDesignerTypes.SegmentTypes.arc) {
          let lastCutoutPoint = cutout.points[cutout.points.length-1]
          if(cutout.isPointsReversed)
            lastCutoutPoint = cutout.points[0]

          // If along at least 25% of the arc or 50mm, include the arc
          if(lastCutoutPoint && MathsUtils.isPointOnSegment(segment, lastCutoutPoint, 0.001)) {
            let joinArcFraction = segment.calculatePointFractionOnSegment(lastCutoutPoint)
            let arcLength = segment.getLength()
            if(joinArcFraction > minJoinArcFraction || joinArcFraction * arcLength > minJoinArcLength)
              useEndSeg = true
          }
        }

        if(useStartSeg) {
          cutout.segmentIdList.push(segmentList[i % segmentList.length].id)

          // Add initial segment to include the corner radius
          if(cutout.pathSegmentList.length == 0) {
            if(segment.type == GlassDesignerTypes.SegmentTypes.line && segment.startCornerRadius > 0) {

              let roundCornerData = MathsUtils.calculateCornerRounding(
                prevSegment.startPosition,
                prevSegment.endPosition,
                segment.startPosition,
                segment.endPosition,
                segment.startCornerRadius,
                false
              )

              if(roundCornerData) {
                // Add in an arc segment to replace the starting corner radius
                var arcSegment = new GlassSegment(GlassDesignerTypes.SegmentTypes.arc)
                arcSegment.arcRadius = roundCornerData.radius
                arcSegment.arcCenterPosition.copy(roundCornerData.center)
                arcSegment.startPosition.copy(roundCornerData.segment1End)
                arcSegment.endPosition.copy(roundCornerData.segment2Start)

                arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
                arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
                arcSegment.arcClockwise = !roundCornerData.clockwise

                // Clear start corner out so it's fully replaced by the arc
                segment.startPosition.copy(arcSegment.endPosition)
                segment.startCornerRadius = 0

                cutout.pathSegmentList.push(arcSegment)
              }
            }
          }

          // If using end segment, include the whole segment
          if(useEndSeg)
            cutout.pathSegmentList.push(segment)


          // Add arc for ending corner radius
          if(segment.type == GlassDesignerTypes.SegmentTypes.line && segment.endCornerRadius > 0) {
            if(useEndSeg && i == cutout.startSegmentIndex+segmentCount-1) {

              let roundCornerData = MathsUtils.calculateCornerRounding(
                segment.startPosition,
                segment.endPosition,
                nextSegment.startPosition,
                nextSegment.endPosition,
                segment.endCornerRadius,
                false
              )

              if(roundCornerData) {
                // Add next segment to list associated with the cutout
                cutout.segmentIdList.push(segmentList[(i+1) % segmentList.length].id)

                // Add in an arc segment to replace the starting corner radius
                let arcSegment = new GlassSegment(GlassDesignerTypes.SegmentTypes.arc)
                arcSegment.arcRadius = roundCornerData.radius
                arcSegment.arcCenterPosition.copy(roundCornerData.center)
                arcSegment.startPosition.copy(roundCornerData.segment1End)
                arcSegment.endPosition.copy(roundCornerData.segment2Start)

                arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
                arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
                arcSegment.arcClockwise = !roundCornerData.clockwise

                // Clear end corner out so it's fully replaced by the arc
                segment.endCornerRadius = 0
                segment.endPosition.copy(arcSegment.startPosition)            

                cutout.pathSegmentList.push(arcSegment)
              }
            }
          }
        }
      }

      


      // Generate the display cutout
      let shape = MathsUtils.generateShapeFromSegmentList(cutout.pathSegmentList, {
        isHole: false,
        ignoreCornerRadius: false
      })
      cutout.displayPointList = shape.getPoints(shapeDivisions)

      // For display purposes, project out the start/end points of the display region & add in any hull vertices in that area
      // Also used for path orientations in CNC cutting
      // Hull vertices may not be in the projection. Which is correct for cutouts in the middle
      // This feels like a hacky solution, but we'll see how it goes
      if(cutout.displayPointList.length >= 2) {
        let startProj = cutout.displayPointList[0]
        let endProj = cutout.displayPointList[cutout.displayPointList.length - 1]

        var edgeNormal = endProj.clone().sub(startProj)
        edgeNormal.normalize()
        var projNormal = new THREE.Vector2(edgeNormal.y, -edgeNormal.x)
        if(MathsUtils.calculateDistanceToPlane2D(projNormal, startProj, hullCenter) > 0)
          projNormal.negate()

        var furthestHullPnt = null
        var furthestHullPntDist = 0

        for(var convexPnt of convexHullPointList) {
          let hullDist = MathsUtils.calculateDistanceToPlane2D(projNormal, startProj, convexPnt)
          if(hullDist < 0)
            continue
          if(MathsUtils.calculateDistanceToPlane2D(edgeNormal, startProj, convexPnt) < 0)
            continue
          if(MathsUtils.calculateDistanceToPlane2D(edgeNormal, endProj, convexPnt) > 0)
            continue

          // Don't add if already in the list
          if(MathsUtils.pointInList(cutout.displayPointList, convexPnt))
            continue

          if(hullDist > furthestHullPntDist || !furthestHullPnt) {
            furthestHullPnt = convexPnt
            furthestHullPntDist = hullDist
          }
        }

        // Add
        if(furthestHullPnt)
          cutout.displayPointList.push(furthestHullPnt)
      }
    })

    return cutoutList
  }

}